"use client";
import React, { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { SearchInput } from "../../../components/ui/search-input";
import { Textarea } from "../../../components/ui/textarea";
// Drawer modal removed - using fixed positioning drawer instead
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui/popover";
import { Checkbox } from "../../../components/ui/checkbox";
import { Calendar } from "../../../components/ui/calender";
import { Label } from "../../../components/ui/label";
import { format } from "date-fns";
import axios from "axios";
import { CreateOrderRequest, Order, ProductItem } from "../types/order";
import { cn } from "../../../lib/utils";
import { API_BASE_URL } from "../../../config/api";
import { useSelector } from "react-redux";
import { ProductForm } from "./ProductForm";
import { coerceLineProducts } from "./OrderProductsDetailsSection";
import { notifyPanelDataChanged } from "../../../utils/panelDataSync";
import { UserForm } from "./UserForm";
import {
  calcOrderTotals,
  calcTotalAreaSqFt,
  getPricePerUnit,
  getProductPlace,
  formatProductDisplayName,
} from "./orderCalculations";
import { ProductLineSpecDisplay, qtyDisplayStyle } from "./ProductLineSpecDisplay";
import { getToBeDeliveredFieldLabel, toApiDate, parseLocalDate } from "../../../utils/dateFormat";
import { normalizeSearchQuery } from "../../../utils/search";
import { Filter } from "lucide-react";
import { useOverlayHistory } from "../../../hooks/useOverlayHistory";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
interface User {
  id: string;
  username: string;
  mobile: string;
  email?: string;
  role: string;
  companyName?: string;
  userCode?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  refresh: () => void;
  userRole: "admin" | "dealer" | "employee";
  userId: string;
  existingOrder?: Order | null;
  /** estimate: hide delivery/quotation/status fields; only customer, products, notes, pricing */
  variant?: "order" | "estimate";
  /** When variant is estimate, called instead of order API */
  onEstimateSave?: (payload: Record<string, unknown>) => Promise<void>;
  /** When set, submit updates the estimate then converts to a new order (full order form). */
  convertFromEstimateId?: string | null;
}

export const OrderForm: React.FC<Props> = ({
  open,
  onClose,
  refresh,
  userRole,
  userId,
  existingOrder,
  variant = "order",
  onEstimateSave,
  convertFromEstimateId = null,
}) => {
  const isEstimateForm = variant === "estimate";
  const isConvertingEstimate = Boolean(convertFromEstimateId);
  const DEFAULT_TERMS_CONDITIONS = `✓ Work completion within agreed timeline
✓ 50% advance, balance on completion
✓ Quality materials guaranteed
✓ Professional workmanship`;

  const [form, setForm] = useState<Partial<CreateOrderRequest>>({
    status: "new",
    created_by: userId,
    products: [],
    filterRole:"all",
    quotation: true,
  });
  
  const [productModalOpen, setProductModalOpen] = useState(false);
    const [userModalOpen, setUserModalOpen] = useState(false);
  const [customers, setCustomers] = useState<User[]>([]);
  const [search, setSearch] = useState("");
  const [appliedCustomerSearch, setAppliedCustomerSearch] = useState("");
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerLoadingMore, setCustomerLoadingMore] = useState(false);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const [customerTotalCount, setCustomerTotalCount] = useState(0);
  const [customerOffset, setCustomerOffset] = useState(0);
  const customerFetchGen = React.useRef(0);
  const CUSTOMER_PAGE_SIZE = 40;
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [users, setUsers] = useState<any>([]);
  const [deliveryDate, setDeliveryDate] = useState<Date>();
  const [toBeDelivered, setToBeDelivered] = useState<Date>();
  const [quotationDate, setQuotationDate] = useState<Date>();
  const [quotationHour, setQuotationHour] = useState("12");
  const [quotationMinute, setQuotationMinute] = useState("00");
  const [quotationAmPm, setQuotationAmPm] = useState<"AM" | "PM">("AM");
  const { authToken = "", id = "" ,role="",username=""} = useSelector(
    (state: any) => state.loginData || {}
  );
  const isDealer = String(role || userRole || "").trim().toLowerCase() === "dealer";
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerFilterOpen, setCustomerFilterOpen] = useState(false);
  const [deliveryDatePopoverOpen, setDeliveryDatePopoverOpen] = useState(false);
  const [toBeDeliveredPopoverOpen, setToBeDeliveredPopoverOpen] = useState(false);
  const [quotationDatePopoverOpen, setQuotationDatePopoverOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ customer?: string; products?: string; deliveryDate?: string }>({});
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationDialogKind, setValidationDialogKind] = useState<"validation" | "save">("validation");
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const formatSaveErrorMessage = (e: unknown): string => {
    const err = e as { message?: string; code?: string; response?: { data?: { message?: string } } };
    if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
      return "Unable to connect to the server. Check your internet connection and ensure the API is running.";
    }
    const api = err?.response?.data?.message;
    if (api && String(api).trim()) return String(api).trim();
    if (err?.message && err.message !== "Network Error") return err.message;
    return isEstimateForm ? "Failed to save quotation. Please try again." : "Failed to save order. Please try again.";
  };

  const showValidationPopup = (fieldErrors: typeof errors) => {
    const labels: string[] = [];
    if (fieldErrors.customer) labels.push("Customer");
    if (fieldErrors.products) {
      labels.push(
        fieldErrors.products === "Please add at least one product" ? "Products" : fieldErrors.products
      );
    }
    if (fieldErrors.deliveryDate) labels.push("Delivery date");
    if (labels.length === 0) return;
    setValidationDialogKind("validation");
    setValidationMessages(labels);
    setValidationDialogOpen(true);
  };

  const showSaveErrorPopup = (message: string) => {
    setValidationDialogKind("save");
    setValidationMessages([message]);
    setValidationDialogOpen(true);
  };

  useOverlayHistory(open, onClose, "order-form");
  useOverlayHistory(customerPickerOpen, () => setCustomerPickerOpen(false), "order-customer-picker");

  const buildQuotationTime = () => `${quotationHour}:${quotationMinute} ${quotationAmPm}`;

  const applyCurrentQuotationTime = () => {
    const now = new Date();
    const hours24 = now.getHours();
    const ampm: "AM" | "PM" = hours24 >= 12 ? "PM" : "AM";
    let hours12 = hours24 % 12;
    if (hours12 === 0) hours12 = 12;
    setQuotationHour(String(hours12).padStart(2, "0"));
    setQuotationMinute(String(now.getMinutes()).padStart(2, "0"));
    setQuotationAmPm(ampm);
  };

  const applyDefaultQuotationDateTime = () => {
    setQuotationDate(new Date());
    applyCurrentQuotationTime();
  };

  const parseQuotationTime = (timeStr?: string) => {
    if (!timeStr) return;
    const match = String(timeStr)
      .trim()
      .replace(/\u202f/g, " ")
      .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    if (!match) return;
    setQuotationHour(match[1].padStart(2, "0"));
    setQuotationMinute(match[2]);
    setQuotationAmPm(match[3].toUpperCase() as "AM" | "PM");
  };

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

    useEffect(() => {
    if (existingOrder) {
      setForm({
        id: existingOrder.id,
        status: existingOrder.status,
        customerId: existingOrder.customerId,
        customerName: existingOrder.customerName,
        customerRole: existingOrder.customerRole,
        price: existingOrder.price,
        gst: existingOrder.gst,
        discount: existingOrder.discount,
        fixing: existingOrder.fixing,
        transport: existingOrder.transport,
        total: existingOrder.total,
        notes: existingOrder.notes,
        siteAddress: existingOrder.siteAddress,
        created_by: existingOrder.createdBy,
        deliveryDate: existingOrder.deliveryDate,
        toBeDelivered: existingOrder.toBeDelivered,
        quotationDate: existingOrder.quotationDate,
        quotationTime: existingOrder.quotationTime,
        quotation: existingOrder.quotation,
        terms: existingOrder.terms || DEFAULT_TERMS_CONDITIONS,
        totalArea: existingOrder.totalArea,
        showTotalAreaInQuotation: existingOrder.showTotalAreaInQuotation,
        advance: existingOrder.advance || "0",
        advance2: existingOrder.advance2 || "0",
        balance: existingOrder.balance || "0",
      });
      setProducts(coerceLineProducts(existingOrder.products || []));
      
      // 🔧 FIX: Parse and set the date state variables from existing order
      if (existingOrder.deliveryDate) {
        try {
          setDeliveryDate(parseLocalDate(existingOrder.deliveryDate));
        } catch (e) {
          console.warn("Failed to parse deliveryDate:", existingOrder.deliveryDate);
        }
      }
      if (existingOrder.toBeDelivered) {
        try {
          setToBeDelivered(parseLocalDate(existingOrder.toBeDelivered));
        } catch (e) {
          console.warn("Failed to parse toBeDelivered:", existingOrder.toBeDelivered);
        }
      }
      if (existingOrder.quotationDate) {
        try {
          setQuotationDate(parseLocalDate(existingOrder.quotationDate));
        } catch (e) {
          console.warn("Failed to parse quotationDate:", existingOrder.quotationDate);
        }
      } else {
        setQuotationDate(new Date());
      }
      if (existingOrder.quotationTime && String(existingOrder.quotationTime).trim()) {
        parseQuotationTime(existingOrder.quotationTime);
      } else {
        applyCurrentQuotationTime();
      }
    } else {
      if (isEstimateForm) {
        applyDefaultQuotationDateTime();
        setForm({
          status: "new",
          created_by: userId,
          products: [],
          filterRole: "all",
          terms: DEFAULT_TERMS_CONDITIONS,
          advance: "0",
          advance2: "0",
          balance: "0",
          quotation: false,
        });
        setProducts([]);
        setDeliveryDate(undefined);
        setToBeDelivered(undefined);
        setErrors({});
        return;
      }
      // Reset for new order - if dealer, auto-select as customer
      applyDefaultQuotationDateTime();

      if (role === "dealer") {
        // Dealers never create quotations — admin enables Create Quotation later
        setForm({
          status: "new",
          created_by: userId,
          customerId: userId,
          customerName: username,
          products: [],
          filterRole: "all",
          terms: DEFAULT_TERMS_CONDITIONS,
          advance: "0",
          advance2: "0",
          balance: "0",
          quotation: false,
        });
      } else {
        setForm({
          status: "new",
          created_by: userId,
          products: [],
          filterRole: "all",
          terms: DEFAULT_TERMS_CONDITIONS,
          advance: "0",
          advance2: "0",
          balance: "0",
          quotation: true,
        });
      }
      setProducts([]);
      // Reset date state variables for new order
      setDeliveryDate(undefined);
      setToBeDelivered(undefined);
    }
  }, [existingOrder, open, role, userId, username, isEstimateForm]);

  /** Strip leading zeros so typing "100" over default 0 never becomes "0100". */
  const sanitizeMoneyInput = (raw: string): string => {
    const cleaned = String(raw ?? "").replace(/[^\d.]/g, "");
    if (cleaned === "" || cleaned === ".") return cleaned;
    const parts = cleaned.split(".");
    let intPart = parts[0] ?? "";
    const decPart = parts.length > 1 ? parts.slice(1).join("") : undefined;
    if (intPart.length > 1) {
      intPart = intPart.replace(/^0+/, "") || "0";
    }
    if (decPart !== undefined) {
      return `${intPart}.${decPart}`;
    }
    return intPart;
  };

  const handleChange = (key: keyof CreateOrderRequest, value: any) => {
    const moneyKeys = new Set([
      "discount",
      "fixing",
      "transport",
      "advance",
      "advance2",
    ]);
    const next =
      moneyKeys.has(String(key)) && typeof value === "string"
        ? sanitizeMoneyInput(value)
        : value;
    setForm((prev) => ({ ...prev, [key]: next }));
  };

  /** Display money fields: blank when zero so the leading 0 does not stick while typing. */
  const moneyDisplay = (v: string | number | undefined | null): string => {
    if (v === undefined || v === null || v === "") return "";
    const s = String(v);
    if (s === "0" || s === "0.0" || s === "0.00") return "";
    return s;
  };

  // Function to calculate total area from width×height products (excludes cloth)
  const calculateTotalArea = () => {
    return calcTotalAreaSqFt(products).toFixed(2);
  };

  const calculateTotals = () => {
    const totals = calcOrderTotals({
      products,
      discount: form.discount,
      fixing: form.fixing,
      transport: form.transport,
      advance: form.advance,
      advance2: form.advance2,
    });

    const totalAreaValue = calculateTotalArea();

    handleChange("price", totals.subtotal.toString());
    handleChange("gst", totals.gst.toString());
    handleChange("total", totals.grandTotal.toString());
    handleChange("balance", totals.balance.toString());
    handleChange("totalArea", totalAreaValue);
  };

  useEffect(() => {
    calculateTotals();
  }, [products, form.discount, form.fixing, form.transport, form.advance, form.advance2]);

  const fetchCustomersPage = React.useCallback(
    async (offset: number, append: boolean, searchQ?: string) => {
      if (!id || !authToken) return;
      const gen = ++customerFetchGen.current;
      if (append) setCustomerLoadingMore(true);
      else setCustomerLoading(true);
      try {
        const roleFilter = form.filterRole || "all";
        const q = normalizeSearchQuery(searchQ ?? appliedCustomerSearch);
        const params: Record<string, string | number> = {
          role: roleFilter,
          firstResults: offset,
          maxResults: CUSTOMER_PAGE_SIZE,
        };
        if (q) params.searchQuery = q;
        const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
          params,
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (gen !== customerFetchGen.current) return;
        const page: User[] = res.data.users || [];
        const total =
          typeof res.data.totalCount === "number"
            ? res.data.totalCount
            : offset + page.length;
        setCustomers((prev) => (append ? [...prev, ...page] : page));
        setCustomerOffset(offset + page.length);
        setCustomerTotalCount(total);
        setCustomerHasMore(offset + page.length < total && page.length > 0);
      } catch (error) {
        console.error("Error fetching users:", error);
        if (gen !== customerFetchGen.current) return;
        if (!append) {
          setCustomers([]);
          setCustomerHasMore(false);
          setCustomerTotalCount(0);
        }
      } finally {
        if (gen === customerFetchGen.current) {
          setCustomerLoading(false);
          setCustomerLoadingMore(false);
        }
      }
    },
    [id, authToken, form.filterRole, appliedCustomerSearch]
  );

  useEffect(() => {
    if (role === "dealer") {
      handleChange("customerId", id);
      handleChange("customerName", username);
    }
  }, [role, id, username]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedCustomerSearch(normalizeSearchQuery(search));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!customerPickerOpen) return;
    setCustomers([]);
    setCustomerOffset(0);
    setCustomerHasMore(true);
    setCustomerTotalCount(0);
    fetchCustomersPage(0, false, appliedCustomerSearch);
  }, [customerPickerOpen, form.filterRole, appliedCustomerSearch, fetchCustomersPage]);

  // Keep a light preload when filter role changes while picker is closed (selected label lookup)
  useEffect(() => {
    if (customerPickerOpen) return;
    fetchCustomersPage(0, false, "");
  }, [form.filterRole]);

  const handleCustomerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!customerHasMore || customerLoading || customerLoadingMore) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom) {
      fetchCustomersPage(customerOffset, true);
    }
  };

  const filteredCustomers = customers;
 const handleSubmit = async (mode: "save" | "submit" | "update" = "submit") => {
  const invoiceGenerated =
    existingOrder?.invoiceGenerated === true ||
    (existingOrder as (Order & { invoice_generated_?: boolean }) | null | undefined)
      ?.invoice_generated_ === true;
  if (!isEstimateForm && invoiceGenerated) {
    onClose();
    return;
  }

  const newErrors: typeof errors = {};

  // Validation checks
  // For dealers, customerId is auto-set, so skip the check
  // For admins/employees, customer must be selected
  if (role !== "dealer" && !form.customerId) {
    newErrors.customer = "Please select a customer";
  }
  if (products.length === 0) {
    newErrors.products = "Please add at least one product";
  }
  if ((!isEstimateForm || isConvertingEstimate) && !deliveryDate && !form.deliveryDate) {
    newErrors.deliveryDate = "Delivery date is required";
  }

  setErrors(newErrors);

  // If there are errors, scroll to the first error field
  if (Object.keys(newErrors).length > 0) {
    showValidationPopup(newErrors);
    setTimeout(() => {
      if (newErrors.customer && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      } else if (newErrors.products && scrollContainerRef.current) {
        // Scroll to products section
        const productsSection = scrollContainerRef.current.querySelector('[data-products-section]');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else if (newErrors.deliveryDate && scrollContainerRef.current) {
        // Scroll to delivery date section
        const dateSection = scrollContainerRef.current.querySelector('[data-date-section]');
        if (dateSection) {
          dateSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
    return;
  }

  const money = (value: unknown, fallback = "0") => {
    if (value === undefined || value === null || value === "") return fallback;
    return String(value);
  };

  const existingStatus = String(existingOrder?.status || form.status || "")
    .trim()
    .toLowerCase();
  let nextStatus = "new";
  if (mode === "save") nextStatus = "saved";
  else if (mode === "submit") nextStatus = "new";
  else nextStatus = existingStatus || "new";

  const payload = {
    status: nextStatus,
    customerId: String(form.customerId || id),
    customerName: form.customerName || username,
    customerRole: form.customerRole || "",
    products,
    price: money(form.price),
    gst: money(form.gst, ""),
    discount: money(form.discount, ""),
    fixing: money(form.fixing, ""),
    transport: money(form.transport, ""),
    total: money(form.total),
    notes: form.notes || "",
    siteAddress: form.siteAddress || "",
    createdBy: String(username || id || ""),
    quotationCreatedBy: !isDealer && form.quotation ? username : "",
    deliveryDate: toApiDate(deliveryDate || form.deliveryDate),
    toBeDelivered: toApiDate(toBeDelivered || form.toBeDelivered),
    // Dealers cannot enable quotation; only admin/employee via Create Quotation checkbox
    quotation: isDealer ? false : Boolean(form.quotation),
    terms: form.terms || "",
    totalArea: money(form.totalArea, ""),
    showTotalAreaInQuotation: Boolean(form.showTotalAreaInQuotation),
    advance: money(form.advance),
    advance2: money(form.advance2),
    balance: money(form.balance),
    quotationDate: toApiDate(quotationDate || form.quotationDate || new Date()),
    quotationTime: buildQuotationTime(),
    // Invoiced orders cannot open this form; keep / restore non-invoice state on save
    invoiceGenerated: false,
    ...(existingOrder?.id && !isConvertingEstimate ? { id: existingOrder.id } : {}),
  };

  try {
    if (isConvertingEstimate && convertFromEstimateId) {
      const estimatePayload = {
        id: convertFromEstimateId,
        customerId: payload.customerId,
        customerName: payload.customerName,
        customerRole: payload.customerRole,
        products: payload.products,
        price: payload.price,
        gst: payload.gst,
        discount: payload.discount,
        fixing: payload.fixing,
        transport: payload.transport,
        total: payload.total,
        notes: payload.notes,
        siteAddress: payload.siteAddress,
        deliveryDate: payload.deliveryDate,
        toBeDelivered: payload.toBeDelivered,
        quotationDate: payload.quotationDate,
        quotationTime: payload.quotationTime,
        terms: payload.terms,
        totalArea: payload.totalArea,
        showTotalAreaInQuotation: payload.showTotalAreaInQuotation,
        advance: payload.advance,
        advance2: payload.advance2,
        balance: payload.balance,
        createdBy: payload.createdBy,
      };
      await axios.put(`${API_BASE_URL}account/${id}/estimate/update`, estimatePayload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await axios.post(
        `${API_BASE_URL}account/${id}/estimate/${convertFromEstimateId}/convert-to-order`,
        {
          deliveryDate: payload.deliveryDate,
          toBeDelivered: payload.toBeDelivered || payload.deliveryDate,
          quotation: payload.quotation,
          quotationDate: payload.quotationDate,
          quotationTime: payload.quotationTime,
          status: "new",
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      notifyPanelDataChanged({ orders: true, resetOrdersPage: true, quotations: true });
    } else if (isEstimateForm && onEstimateSave) {
      const estimatePayload = {
        customerId: payload.customerId,
        customerName: payload.customerName,
        customerRole: payload.customerRole,
        products: payload.products,
        price: payload.price,
        gst: payload.gst,
        discount: payload.discount,
        fixing: payload.fixing,
        transport: payload.transport,
        total: payload.total,
        notes: payload.notes,
        siteAddress: payload.siteAddress,
        deliveryDate: payload.deliveryDate,
        toBeDelivered: payload.toBeDelivered,
        quotationDate: payload.quotationDate,
        quotationTime: payload.quotationTime,
        terms: payload.terms,
        totalArea: payload.totalArea,
        showTotalAreaInQuotation: payload.showTotalAreaInQuotation,
        advance: payload.advance,
        advance2: payload.advance2,
        balance: payload.balance,
        createdBy: payload.createdBy,
        ...(existingOrder?.id ? { id: existingOrder.id } : {}),
      };
      await onEstimateSave(estimatePayload);
    } else if (existingOrder?.id && !isConvertingEstimate) {
      await axios.put(`${API_BASE_URL}account/${id}/order/update`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } else {
      await axios.post(`${API_BASE_URL}account/${id}/order/create`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    }
  } catch (e: unknown) {
    const apiMessage = formatSaveErrorMessage(e);
    console.error("Error saving:", apiMessage, e);
    if (isConvertingEstimate) {
      alert(apiMessage);
      return;
    }
    showSaveErrorPopup(apiMessage);
    return;
  }

  refresh();
  onClose();
};

  const isDraftOrCreate =
    !existingOrder ||
    String(existingOrder.status || "").trim().toLowerCase() === "saved";
  const isEditingLiveOrder =
    !!existingOrder &&
    String(existingOrder.status || "").trim().toLowerCase() !== "saved";


  return (
    <>
      {/* Drawer Overlay */}
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      )}

      <div
        className={`fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 ${
          open ? "pointer-events-none opacity-100 visible" : "pointer-events-none invisible opacity-0"
        }`}
      >
      <div
        className={`pointer-events-auto bg-white w-full md:w-[65vw] max-h-[90vh] rounded-t-2xl md:rounded-lg flex flex-col shadow-2xl ${
          open ? "translate-y-0" : "translate-y-full md:translate-y-0"
        }`}
      >
        {/* Header with gradient */}
        <div className="px-6 py-5 border-b-2 border-blue-800/30 shrink-0 flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="text-2xl font-bold text-black drop-shadow-lg">
            {isConvertingEstimate
              ? "🔄 Convert quotation to order"
              : isEstimateForm
                ? existingOrder
                  ? "📝 Edit Quotation"
                  : "➕ Create New Quotation"
                : existingOrder
                  ? "📝 Edit Order"
                  : "➕ Create New Order"}
          </h2>
          <button
            onClick={onClose}
            className="text-black hover:text-blue-600 text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="space-y-5 px-6 py-5 flex-1 min-h-0 overflow-y-auto overflow-x-hidden pointer-events-auto pb-8" ref={scrollContainerRef} data-customer-section>
          {/* Customer dropdown */}

          <div className={`bg-white p-4 rounded-xl border-2 shadow-sm hover:shadow-md transition-all ${
            errors.customer ? "border-red-400 bg-red-50" : "border-blue-200/50"
          }`}>
            <Label className="font-bold text-gray-800 mb-2 block">👤 Customer</Label>
            {role === "dealer" ? (
              // For dealers, show a non-editable display
              <div className="w-full p-3 h-12 rounded-lg border-2 border-blue-300 bg-blue-50 font-semibold text-gray-700 flex items-center">
                {form.customerName || "Loading..."}
              </div>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between border-2 border-blue-300 hover:border-blue-500 h-12 rounded-lg font-semibold text-gray-700"
                  onClick={() => {
                    setSearch("");
                    setCustomerFilterOpen(false);
                    setCustomerPickerOpen(true);
                  }}
                >
                  <span className="truncate text-left">
                    {form.customerId
                      ? (() => {
                          const selected = customers.find((c) => String(c.id) === String(form.customerId));
                          const label = form.customerName || "Select Customer";
                          return selected?.userCode ? `${label} (${selected.userCode})` : label;
                        })()
                      : form.customerName || "Select Customer"}
                  </span>
                  <span className="ml-2 shrink-0 text-gray-500">▾</span>
                </Button>

                {customerPickerOpen && (
                  <div
                    className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40"
                    onClick={() => {
                      setCustomerPickerOpen(false);
                      setCustomerFilterOpen(false);
                    }}
                  >
                    <div
                      className="bg-white w-full max-w-xl h-[min(90vh,640px)] rounded-2xl shadow-2xl border-2 border-blue-200 flex flex-col overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-5 py-4 border-b-2 border-blue-100 flex items-center justify-between shrink-0">
                        <h3 className="text-lg font-bold text-gray-900">Select Customer</h3>
                        <button
                          type="button"
                          onClick={() => {
                            setCustomerPickerOpen(false);
                            setCustomerFilterOpen(false);
                          }}
                          className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="p-4 border-b border-blue-100 shrink-0 space-y-2">
                        <div className="flex items-center gap-2">
                          <SearchInput
                            autoFocus
                            placeholder="🔍 Search name, email, mobile, or code..."
                            value={search}
                            onValueChange={setSearch}
                            className="border-2 border-blue-300 rounded-lg h-11 font-medium flex-1 min-w-0"
                          />
                          <button
                            type="button"
                            onClick={() => setCustomerFilterOpen((prev) => !prev)}
                            className={cn(
                              "h-11 w-11 shrink-0 rounded-lg border-2 flex items-center justify-center",
                              customerFilterOpen || (form.filterRole && form.filterRole !== "all")
                                ? "border-blue-500 bg-blue-50 text-blue-700"
                                : "border-blue-300 text-gray-600 hover:bg-blue-50"
                            )}
                            title="Filter customers"
                            aria-label="Filter customers"
                          >
                            <Filter size={18} />
                          </button>
                        </div>
                        {customerFilterOpen && (
                          <select
                            className="w-full border-2 border-blue-300 rounded-lg p-2.5 text-sm bg-white font-semibold text-gray-700"
                            onChange={(e) => setForm((prev) => ({ ...prev, filterRole: e.target.value }))}
                            value={form.filterRole || "all"}
                          >
                            <option value="all">All Customers</option>
                            <option value="dealer">Dealers</option>
                            <option value="end-user">End Users</option>
                            <option value="interior">Interior</option>
                          </select>
                        )}
                        <p className="text-xs font-semibold text-blue-700">
                          {customerLoading && customers.length === 0
                            ? "Loading..."
                            : `${customers.length}${
                                customerTotalCount ? ` of ${customerTotalCount}` : ""
                              } customers`}
                          {form.filterRole && form.filterRole !== "all" ? ` · ${form.filterRole}` : ""}
                          {normalizeSearchQuery(search) &&
                          normalizeSearchQuery(search) !== appliedCustomerSearch
                            ? " · typing..."
                            : ""}
                        </p>
                      </div>

                      <div
                        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2"
                        onScroll={handleCustomerScroll}
                      >
                        {customerLoading && customers.length === 0 ? (
                          <div className="py-12 text-center text-sm text-blue-700 font-medium">
                            Loading customers...
                          </div>
                        ) : filteredCustomers.length === 0 ? (
                          <div className="py-12 text-center text-sm text-gray-500">
                            No matching customers
                          </div>
                        ) : (
                          <>
                          {filteredCustomers.map((u) => (
                            <div
                              key={u.id}
                              className={cn(
                                "p-3 rounded-xl cursor-pointer hover:bg-blue-50 transition-all border-2",
                                form.customerId === u.id
                                  ? "bg-blue-50 border-blue-500 shadow-sm"
                                  : "border-transparent hover:border-blue-200"
                              )}
                              onClick={() => {
                                handleChange("customerId", u.id.toString());
                                handleChange("customerName", u.username);
                                handleChange("customerRole", u.role);
                                setCustomerPickerOpen(false);
                                setCustomerFilterOpen(false);
                                setSearch("");
                              }}
                            >
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="font-bold text-gray-900">
                                  {u.companyName || u.username}
                                </span>
                                {u.userCode ? (
                                  <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                                    {u.userCode}
                                  </span>
                                ) : null}
                                <span className="text-xs font-semibold text-emerald-700 capitalize">
                                  {u.role}
                                </span>
                              </div>
                              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm text-gray-600">
                                {u.companyName && u.username ? (
                                  <span>
                                    <span className="font-semibold text-gray-500">User:</span> {u.username}
                                  </span>
                                ) : null}
                                {u.email ? (
                                  <span className="truncate">
                                    <span className="font-semibold text-gray-500">Email:</span> {u.email}
                                  </span>
                                ) : null}
                                {u.mobile ? (
                                  <span>
                                    <span className="font-semibold text-gray-500">Mobile:</span> {u.mobile}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          ))}
                          {customerLoadingMore ? (
                            <div className="py-3 text-center text-xs font-medium text-blue-700">
                              Loading more...
                            </div>
                          ) : null}
                          {!customerHasMore && customers.length > 0 ? (
                            <div className="py-2 text-center text-[11px] text-gray-400">End of list</div>
                          ) : null}
                          </>
                        )}
                      </div>

                      <div className="border-t-2 border-blue-100 p-4 flex justify-end bg-blue-50 shrink-0">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            setCustomerPickerOpen(false);
                            setCustomerFilterOpen(false);
                            setUserModalOpen(true);
                          }}
                          className="bg-blue-600 text-white hover:bg-blue-700 font-bold"
                        >
                          + Add Customer
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

            <div className="bg-white p-4 rounded-xl border-2 border-teal-200/50 shadow-sm hover:shadow-md transition-all">
            <Label className="font-bold text-gray-800 mb-2 block">Site Address</Label>
            <Textarea
              value={form.siteAddress || ""}
              placeholder="Installation / job site address"
              onChange={(e) => handleChange("siteAddress", e.target.value)}
              className="border-2 border-teal-300 rounded-lg min-h-24 focus:border-teal-500 font-medium text-gray-700"
            />
            <p className="text-xs text-gray-500 mt-1">Shown on quotation PDF under Customer Details (after mobile).</p>
          </div>

            <div className="bg-white p-4 rounded-xl border-2 border-purple-200/50 shadow-sm hover:shadow-md transition-all">
            <Label className="font-bold text-gray-800 mb-2 block">Note</Label>
            <Textarea
             value={form.notes || ""}
              placeholder="Shown on Production Copy under Note"
              onChange={(e) => handleChange("notes", e.target.value)}
              className="border-2 border-purple-300 rounded-lg min-h-24 focus:border-purple-500 font-medium text-gray-700"
            />
            <p className="text-xs text-gray-500 mt-1">Printed on Production Copy. A blank line is added at the end.</p>
          </div>

          {/* Products */}
          {/* 🧾 Products Table Section */}
          <div className={`border-2 rounded-xl p-5 shadow-sm bg-gradient-to-br from-green-50 via-white to-cyan-50 ${
            errors.products ? "border-red-400" : "border-green-200/50"
          }`} data-products-section>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-xl text-gray-800 flex items-center gap-2">🛍️ Products</h3>
              <Button 
                onClick={() => setProductModalOpen(true)}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold hover:shadow-lg hover:shadow-green-500/40 transition-all"
              >
                + Add Product
              </Button>
            </div>

            {products.length === 0 ? (
              <p className="text-gray-500 text-center py-8 font-semibold">No products added yet. Click the button above to add products.</p>
            ) : (
              <div className="overflow-x-auto -mx-1 px-1">
                <table className="w-full text-sm table-auto">
                  <thead>
                    <tr className="bg-gradient-to-r from-green-100 to-emerald-100 border-b-2 border-green-300">
                      <th className="px-2 md:px-4 py-3 text-left font-bold text-gray-800 w-8 whitespace-nowrap">#</th>
                      <th className="px-2 md:px-4 py-3 text-left font-bold text-gray-800 min-w-[280px] w-[55%] md:min-w-72">Product Name</th>
                      {role != "dealer" && <>
                      <th className="px-2 md:px-4 py-3 text-left font-bold text-gray-800 whitespace-nowrap w-12">Qty</th>
                      <th className="px-2 md:px-4 py-3 text-left font-bold text-gray-800 whitespace-nowrap w-16">Area</th>
                      <th className="px-2 md:px-4 py-3 text-left font-bold text-gray-800 whitespace-nowrap hidden sm:table-cell">UOM</th>
                      <th className="px-2 md:px-4 py-3 text-left font-bold text-gray-800 whitespace-nowrap hidden md:table-cell">Price/Unit</th>
                      <th className="px-2 md:px-4 py-3 text-left font-bold text-gray-800 whitespace-nowrap hidden md:table-cell">Amount</th>
                      </>}
                      <th className="px-2 md:px-4 py-3 text-center font-bold text-gray-800 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, i) => (
                      <tr
                        key={i}
                        className="border-b border-green-100 hover:bg-green-50/80 transition-all"
                      >
                        <td className="px-2 md:px-4 py-3 font-bold text-gray-700 whitespace-nowrap">{i + 1}</td>
                        <td className="px-2 md:px-4 py-3 min-w-[280px] w-[55%] md:min-w-72 align-top">
                          <div className="font-bold text-gray-900">
                            {formatProductDisplayName(p.name, getProductPlace(p))}
                          </div>
                          <ProductLineSpecDisplay product={p as unknown as Record<string, unknown>} />
                        </td>
                       {role != "dealer" && <>
                       <td className="px-2 md:px-4 py-3 whitespace-nowrap" style={qtyDisplayStyle()}>{p.nos}</td>
                        <td className="px-2 md:px-4 py-3 text-gray-700 font-semibold whitespace-nowrap">{parseFloat(p.area ?? "0").toFixed(2)}</td>
                        <td className="px-2 md:px-4 py-3 text-gray-700 font-semibold whitespace-nowrap hidden sm:table-cell">{p.area_uom}</td>
                        <td className="px-2 md:px-4 py-3 font-bold text-blue-600 whitespace-nowrap hidden md:table-cell">₹{getPricePerUnit(p).toFixed(2)}</td>
                        <td className="px-2 md:px-4 py-3 font-bold text-emerald-600 whitespace-nowrap hidden md:table-cell">₹{parseFloat(p.price.toString()).toFixed(2)}</td>
                       </> }
                        <td className="px-4 py-3 text-center flex justify-center gap-2 flex-wrap">
                          {/* ✏️ Edit */}
                          <Button
  size="sm"
  variant="outline"
  onClick={() => {
    setEditingProduct(p);
    setEditingIndex(i);
    setProductModalOpen(true);
  }}
  className="border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-bold"
>
  Edit
</Button>


                          {/* 📋 Duplicate */}
                          <Button
                            size="sm"
                            onClick={() => {
                              const clone = { ...p };
                              setProducts([...products.slice(0, i + 1), clone, ...products.slice(i + 1)]);
                            }}
                            className="bg-cyan-500 text-white hover:bg-cyan-600 font-bold"
                          >
                            Duplicate
                          </Button>

                          {/* 🗑️ Delete */}
                          <Button
                            size="sm"
                            onClick={() =>
                              setProducts(products.filter((_, idx) => idx !== i))
                            }
                            className="bg-red-500 text-white hover:bg-red-600 font-bold"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>


          {/* Price summary */}
         { role != "dealer" && <div className="bg-gradient-to-br from-orange-50 via-white to-amber-50 border-2 border-orange-200/50 rounded-xl p-5 shadow-sm">
            <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">💰 Price Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white border-2 border-orange-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Total (₹)</Label>
              <Input
                placeholder="0.00"
                value={form.price ? parseFloat(form.price.toString()).toFixed(2) : ""}
                onChange={(e) => handleChange("price", e.target.value)}
                className="border-0 text-lg font-bold text-orange-600 focus:ring-0 p-0"
                readOnly
              />
            </div>
            <div className="bg-white border-2 border-blue-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">GST (₹)</Label>
              <Input
                placeholder="0.00"
                value={form.gst ? parseFloat(form.gst.toString()).toFixed(2) : ""}
                onChange={(e) => handleChange("gst", e.target.value)}
                className="border-0 text-lg font-bold text-blue-600 focus:ring-0 p-0"
                readOnly
              />
            </div>
            <div className="bg-white border-2 border-red-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Discount (₹)</Label>
              <Input
                placeholder="0"
                value={moneyDisplay(form.discount)}
                onChange={(e) => handleChange("discount", e.target.value)}
                className="border-2 border-red-300 text-lg font-bold text-red-600 focus:ring-0"
                type="text"
                inputMode="decimal"
              />
            </div>
            </div>
            {/* Fixing / Transport / Advance 1 / Advance 2 — single row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white border-2 border-purple-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Fixing (₹)</Label>
              <Input
                value={moneyDisplay(form.fixing)}
                placeholder="0"
                onChange={(e) => handleChange("fixing", e.target.value)}
                className="border-2 border-purple-300 text-lg font-bold text-purple-600 focus:ring-0"
                type="text"
                inputMode="decimal"
              />
            </div>
            <div className="bg-white border-2 border-green-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Transport (₹)</Label>
              <Input
                value={moneyDisplay(form.transport)}
                placeholder="0"
                onChange={(e) => handleChange("transport", e.target.value)}
                className="border-2 border-green-300 text-lg font-bold text-green-600 focus:ring-0"
                type="text"
                inputMode="decimal"
              />
            </div>
            <div className="bg-white border-2 border-teal-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Advance 1 (₹)</Label>
              <Input
                value={moneyDisplay(form.advance)}
                placeholder="0"
                onChange={(e) => handleChange("advance", e.target.value)}
                className="border-2 border-teal-300 text-lg font-bold text-teal-700 focus:ring-0"
                type="text"
                inputMode="decimal"
              />
            </div>
            <div className="bg-white border-2 border-teal-300 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Advance 2 (₹)</Label>
              <Input
                value={moneyDisplay(form.advance2)}
                placeholder="0"
                onChange={(e) => handleChange("advance2", e.target.value)}
                className="border-2 border-teal-300 text-lg font-bold text-teal-700 focus:ring-0"
                type="text"
                inputMode="decimal"
              />
            </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg p-3 text-white">
              <Label className="text-xs font-bold text-cyan-100 block mb-1">GRAND TOTAL (₹)</Label>
              <div className="text-lg font-bold break-words">₹{form.total ? parseFloat(form.total.toString()).toFixed(2) : "0.00"}</div>
            </div>
            <div className="bg-white border-2 border-slate-400 rounded-lg p-3">
              <Label className="text-xs font-bold text-gray-600 block mb-1">Balance Amount (₹)</Label>
              <div className="text-lg font-bold text-slate-800">
                ₹{form.balance ? parseFloat(form.balance.toString()).toFixed(2) : "0.00"}
              </div>
              <p className="text-xs text-gray-500 mt-1">Grand Total − Advance 1 − Advance 2</p>
            </div>
            </div>
          </div>}

          {/* Total Area Field */}
          {role != "dealer" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border-2 border-slate-300 shadow-sm hover:shadow-md transition-all">
                <Label className="font-bold text-gray-800 mb-2 block">📋 Total Area (Sq Ft)</Label>
                <div className="w-full p-3 h-12 rounded-lg border-2 border-slate-400 bg-white font-bold text-slate-700 flex items-center">
                  {form.totalArea ? parseFloat(form.totalArea).toFixed(2) : "0.00"} sq ft
                </div>
                <p className="text-xs text-gray-600 mt-2">ℹ️ Width×Height products only (cloth excluded)</p>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-2 border-slate-200/50 rounded-xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-all">
                <Checkbox
                  className="w-6 h-6 border-2 border-slate-400 rounded-lg"
                  checked={form.showTotalAreaInQuotation || false}
                  onCheckedChange={(checked) => handleChange("showTotalAreaInQuotation", !!checked)}
                />
                <Label className="font-bold text-gray-800 cursor-pointer">Show Total Area in Quotation</Label>
              </div>
            </div>
          )}

          {/* Dates — optional on estimates; required on order submit / convert */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-date-section>
            <div className={`bg-gradient-to-br p-5 rounded-2xl border-2 shadow-lg hover:shadow-xl transition-all ${
              !isEstimateForm && errors.deliveryDate 
                ? "from-red-50 to-red-50 border-red-400 bg-red-50" 
                : "from-blue-50 to-cyan-50 border-blue-300/60"
            }`}>
              <Label className="font-bold text-gray-800 mb-3 block text-sm">
                📅 Delivery Date{isEstimateForm ? " (optional)" : ""}
              </Label>
              <Popover open={deliveryDatePopoverOpen} onOpenChange={setDeliveryDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-14 border-2 border-blue-400 hover:border-blue-600 hover:bg-blue-50 font-bold text-gray-800 rounded-xl justify-between px-4 text-base shadow-md hover:shadow-lg transition-all duration-200 active:scale-95">
                    <span>{deliveryDate ? format(deliveryDate, "PPP") : form?.deliveryDate ? format(form?.deliveryDate, "PPP") : "Pick date"}</span>
                    <span className="text-blue-500">📆</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="bg-white rounded-xl shadow-2xl border-2 border-blue-300 p-2 w-fit flex flex-col items-center">
                  <Calendar
                    mode="single"
                    selected={deliveryDate}
                    onSelect={(date) => {
                      setDeliveryDate(date);
                      setDeliveryDatePopoverOpen(false);
                    }}
                    className="bg-white rounded-lg p-2 border border-blue-200"
                  />
                </PopoverContent>
              </Popover>
            </div>

           {role != "dealer" &&  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-2xl border-2 border-purple-300/60 shadow-lg hover:shadow-xl transition-all">
              <Label className="font-bold text-gray-800 mb-3 block text-sm">🚚 {getToBeDeliveredFieldLabel(form.customerRole)}</Label>
              <Popover open={toBeDeliveredPopoverOpen} onOpenChange={setToBeDeliveredPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-14 border-2 border-purple-400 hover:border-purple-600 hover:bg-purple-50 font-bold text-gray-800 rounded-xl justify-between px-4 text-base shadow-md hover:shadow-lg transition-all duration-200 active:scale-95">
                    <span>{toBeDelivered ? format(toBeDelivered, "PPP") : form?.toBeDelivered ? format(form?.toBeDelivered, "PPP") : "Pick date"}</span>
                    <span className="text-purple-500">🗓️</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="bg-white rounded-xl shadow-2xl border-2 border-purple-300 p-2 w-fit flex flex-col items-center">
                  <Calendar
                    mode="single"
                    selected={toBeDelivered}
                    onSelect={(date) => {
                      setToBeDelivered(date);
                      setToBeDeliveredPopoverOpen(false);
                    }}
                    className="bg-white rounded-lg p-2 border border-purple-200"
                  />
                </PopoverContent>
              </Popover>
            </div>}
          </div>

          {role != "dealer" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-2xl border-2 border-amber-300/60 shadow-lg">
                <Label className="font-bold text-gray-800 mb-3 block text-sm">
                  📝 Quotation Date{isEstimateForm ? " (optional)" : ""}
                </Label>
                <Popover open={quotationDatePopoverOpen} onOpenChange={setQuotationDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-14 border-2 border-amber-400 hover:border-amber-600 hover:bg-amber-50 font-bold text-gray-800 rounded-xl justify-between px-4 text-base shadow-md">
                      <span>{quotationDate ? format(quotationDate, "PPP") : "Pick date"}</span>
                      <span className="text-amber-500">📅</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="bg-white rounded-xl shadow-2xl border-2 border-amber-300 p-2 w-fit flex flex-col items-center">
                    <Calendar
                      mode="single"
                      selected={quotationDate}
                      onSelect={(date) => {
                        setQuotationDate(date);
                        setQuotationDatePopoverOpen(false);
                      }}
                      className="bg-white rounded-lg p-2 border border-amber-200"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-2xl border-2 border-amber-300/60 shadow-lg">
                <Label className="font-bold text-gray-800 mb-3 block text-sm">🕐 Quotation Time (12-hour)</Label>
                <div className="flex gap-2 items-center">
                  <select
                    value={quotationHour}
                    onChange={(e) => setQuotationHour(e.target.value)}
                    className="flex-1 h-14 border-2 border-amber-400 rounded-xl font-bold text-gray-800 px-3 bg-white"
                  >
                    {Array.from({ length: 12 }, (_, i) => {
                      const h = String(i + 1).padStart(2, "0");
                      return <option key={h} value={h}>{h}</option>;
                    })}
                  </select>
                  <span className="font-bold text-lg">:</span>
                  <select
                    value={quotationMinute}
                    onChange={(e) => setQuotationMinute(e.target.value)}
                    className="flex-1 h-14 border-2 border-amber-400 rounded-xl font-bold text-gray-800 px-3 bg-white"
                  >
                    {Array.from({ length: 60 }, (_, i) => {
                      const m = String(i).padStart(2, "0");
                      return <option key={m} value={m}>{m}</option>;
                    })}
                  </select>
                  <select
                    value={quotationAmPm}
                    onChange={(e) => setQuotationAmPm(e.target.value as "AM" | "PM")}
                    className="w-20 h-14 border-2 border-amber-400 rounded-xl font-bold text-gray-800 px-2 bg-white"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          { role != "dealer" && (
            <div className="bg-white p-4 rounded-xl border-2 border-indigo-200/50 shadow-sm hover:shadow-md transition-all">
              <Label className="font-bold text-gray-800 mb-3 block">📋 Terms & Conditions</Label>
              <Textarea
                value={form.terms || ""}
                onChange={(e) => handleChange("terms", e.target.value)}
                placeholder="Enter terms and conditions..."
                className="border-2 border-indigo-300 rounded-lg min-h-32 focus:border-indigo-500 font-medium text-gray-700"
              />
              <p className="text-xs text-gray-500 mt-2">💡 Tip: Use bullet points (•) to format conditions</p>
            </div>
          )}

          {!isEstimateForm && !isDealer && (
            <div className="bg-gradient-to-r from-pink-50 to-rose-50 border-2 border-pink-400 rounded-xl p-4 flex items-center gap-3 shadow-sm">
              <Checkbox
                id="create-quotation-end"
                className="w-6 h-6 border-2 border-pink-500 rounded-lg bg-white"
                checked={!!form.quotation}
                onCheckedChange={(checked) => handleChange("quotation", !!checked)}
              />
              <Label htmlFor="create-quotation-end" className="font-bold text-gray-800 cursor-pointer text-base">
                📄 Create Quotation
              </Label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t-2 border-gray-200 bg-gray-50 rounded-b-2xl md:rounded-b-lg flex-shrink-0">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-2 border-gray-400 hover:bg-gray-100 font-bold text-gray-700 h-11 rounded-lg"
          >
            ✕ Cancel
          </Button>
          {isEstimateForm ? (
            <Button
              type="button"
              onClick={() => handleSubmit("submit")}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold hover:shadow-lg hover:shadow-emerald-500/40 transition-all h-11 rounded-lg px-6"
            >
              ✓ Save Quotation
            </Button>
          ) : isConvertingEstimate ? (
            <Button
              type="button"
              onClick={() => handleSubmit("submit")}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold hover:shadow-lg hover:shadow-blue-500/40 transition-all h-11 rounded-lg px-6"
            >
              ✓ Submit Order
            </Button>
          ) : isDraftOrCreate ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSubmit("save")}
                className="border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-bold h-11 rounded-lg px-5"
              >
                Save Order
              </Button>
              <Button
                type="button"
                onClick={() => handleSubmit("submit")}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold hover:shadow-lg hover:shadow-blue-500/40 transition-all h-11 rounded-lg px-6"
              >
                ✓ Submit Order
              </Button>
            </>
          ) : isEditingLiveOrder ? (
            <Button
              type="button"
              onClick={() => handleSubmit("update")}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold hover:shadow-lg hover:shadow-blue-500/40 transition-all h-11 rounded-lg px-6"
            >
              ✓ Update Order
            </Button>
          ) : null}
        </div>
      </div>
      </div>

       <ProductForm
  open={productModalOpen}
  onClose={() => {
    setProductModalOpen(false);
    setEditingProduct(null);
    setEditingIndex(null);
  }}
  onAdd={(product) => {
    const items = product as unknown as ProductItem[];
    const updatedLine = items[0];
    if (editingIndex != null && updatedLine) {
      setProducts((prev) =>
        prev.map((p, idx) =>
          idx === editingIndex ? { ...p, ...updatedLine } : p
        )
      );
    } else {
      setProducts((prev) => [...prev, ...items]);
    }
    setEditingProduct(null);
    setEditingIndex(null);
    setProductModalOpen(false);
  }}
  existingProduct={editingProduct as any}
  customerRole={form.customerRole || ""}
  userRole={userRole}
  otherModalOpen={userModalOpen}
/>

        <UserForm
          open={userModalOpen}
          onClose={() => setUserModalOpen(false)}
          onAdd={(user: any) => {
            setUsers([...users, user]);
            setCustomerPickerOpen(false);
            setUserModalOpen(false);
            handleChange("customerId", user.id);
            handleChange("customerName", user.username);
            fetchCustomersPage(0, false, "");
          }}
        />

      <Dialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
        <DialogContent
          overlayClassName="z-[100] bg-black/60"
          className="z-[101] sm:max-w-md border-2 border-blue-200 bg-white shadow-2xl rounded-xl p-6"
          showCloseButton
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-blue-900 text-xl font-bold">
              {validationDialogKind === "validation"
                ? "Required fields missing"
                : "Unable to save"}
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-sm">
              {validationDialogKind === "validation"
                ? "Please complete the following before saving:"
                : "Please review the message below and try again."}
            </DialogDescription>
          </DialogHeader>
          {validationDialogKind === "validation" ? (
            <ul className="list-disc pl-5 space-y-2 text-sm font-semibold text-gray-900 my-1">
              {validationMessages.map((msg, i) => (
                <li key={i} className="break-words">
                  {msg}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm font-medium text-gray-800 my-2 break-words leading-relaxed">
              {validationMessages[0]}
            </p>
          )}
          <DialogFooter className="mt-2 sm:justify-end">
            <Button
              type="button"
              onClick={() => setValidationDialogOpen(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold min-w-[88px]"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
